import React, { createContext, useContext, ReactNode } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useQuarterlyReport } from "~/api/quarterly_reports"
import type { QuarterlyReport } from "~/api/quarterly_reports"

interface QuarterlyReportContextType {
  reportId: string
  report: QuarterlyReport | undefined
  isLoading: boolean
  error: Error | null
  mock: boolean
}

const QuarterlyReportContext = createContext<
  QuarterlyReportContextType | undefined
>(undefined)

interface QuarterlyReportProviderProps {
  children: ReactNode
  reportId?: string
}

export const QuarterlyReportProvider: React.FC<
  QuarterlyReportProviderProps
> = ({ children, reportId: propsReportId }) => {
  const { reportId: urlReportId } = useParams<{
    reportId: string
  }>()
  const [searchParams] = useSearchParams()

  const reportId = propsReportId || urlReportId || ""
  const mock = searchParams.get("mock") === "true"
  const debug = searchParams.get("debug") === "true"

  const {
    data: report,
    error,
    isLoading,
  } = useQuarterlyReport(reportId, { mock, debug })

  const value = {
    reportId,
    report,
    isLoading,
    error,
    mock,
  }

  return (
    <QuarterlyReportContext.Provider value={value}>
      {children}
    </QuarterlyReportContext.Provider>
  )
}

export const useQuarterlyReportContext = (): QuarterlyReportContextType => {
  const context = useContext(QuarterlyReportContext)
  if (context === undefined) {
    throw new Error(
      "useQuarterlyReportContext must be used within a QuarterlyReportProvider",
    )
  }
  return context
}
